<template>
    <section
        class="point-flow"
        :style="{
            '--pf-home': homeColor,
            '--pf-away': awayColor,
        }"
    >
        <h2 class="point-flow__title">Ход игры</h2>

        <div class="point-flow__body">
            <div class="point-flow__sets" role="tablist" aria-label="Партии">
                <button
                    v-for="setNum in setNumbers"
                    :key="setNum"
                    type="button"
                    role="tab"
                    class="point-flow__set-btn"
                    :class="{ 'is-active': selectedSet === setNum }"
                    :aria-selected="selectedSet === setNum"
                    @click="selectedSet = setNum"
                >
                    {{ setNum }} сет
                </button>
            </div>

            <div class="point-flow__chart-wrap">
                <div class="point-flow__chart" role="img" :aria-label="chartAriaLabel">
                    <div class="point-flow__axis" aria-hidden="true" />
                    <div
                        v-for="(run, index) in currentRuns"
                        :key="`${selectedSet}-${index}-${run.team}-${run.length}`"
                        class="point-flow__col"
                    >
                        <div class="point-flow__half point-flow__half--home">
                            <template v-if="run.team === 'home'">
                                <span class="point-flow__label">{{ run.length }}</span>
                                <div
                                    class="point-flow__bar point-flow__bar--home"
                                    :style="{ height: barHeight(run.length) }"
                                />
                            </template>
                        </div>
                        <div class="point-flow__half point-flow__half--away">
                            <template v-if="run.team === 'away'">
                                <div
                                    class="point-flow__bar point-flow__bar--away"
                                    :style="{ height: barHeight(run.length) }"
                                />
                                <span class="point-flow__label">{{ run.length }}</span>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <div class="point-flow__score">
                <span class="point-flow__score-home">{{ currentScore.home }}</span>
                <span class="point-flow__score-away">{{ currentScore.away }}</span>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
    pointFlow: {
        type: Object,
        required: true,
    },
    homeColor: {
        type: String,
        default: '#275092',
    },
    awayColor: {
        type: String,
        default: '#06b6d4',
    },
});

const setNumbers = computed(() =>
    Object.keys(props.pointFlow?.sets ?? {})
        .map(Number)
        .filter((n) => Number.isFinite(n) && n > 0)
        .sort((a, b) => a - b)
);

const selectedSet = ref(setNumbers.value[0] ?? 1);

watch(
    setNumbers,
    (nums) => {
        if (!nums.includes(selectedSet.value)) {
            selectedSet.value = nums[0] ?? 1;
        }
    },
    { immediate: true }
);

const currentSetData = computed(() => props.pointFlow?.sets?.[selectedSet.value] ?? null);

const currentRuns = computed(() => currentSetData.value?.runs ?? []);

const currentScore = computed(() => currentSetData.value?.score ?? { home: 0, away: 0 });

const maxRunLength = computed(() =>
    Math.max(1, ...currentRuns.value.map((run) => Number(run.length) || 0))
);

const UNIT_PX = 14;
const MAX_BAR_PX = 72;

const barHeight = (length) => {
    const n = Number(length) || 0;
    if (maxRunLength.value * UNIT_PX > MAX_BAR_PX) {
        return `${Math.max(UNIT_PX, (n / maxRunLength.value) * MAX_BAR_PX)}px`;
    }
    return `${Math.max(UNIT_PX, Math.min(MAX_BAR_PX, n * UNIT_PX))}px`;
};

const chartAriaLabel = computed(() => {
    const { home, away } = currentScore.value;
    return `Ход ${selectedSet.value} сета, счёт ${home}:${away}`;
});
</script>

<style lang="scss" scoped>
@import '../../../scss/tokens';

.point-flow {
    margin: 1rem 0 1.25rem;
    padding: 1rem 1rem 1.1rem;
    border-radius: $radius-lg;
    background: $color-bg-secondary;
    color: $color-text-primary;
    border: 1px solid rgba(15, 23, 42, 0.06);
}

.point-flow__title {
    margin: 0 0 0.85rem;
    font-size: 1.05rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    color: $color-text-primary;
}

.point-flow__body {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 0.75rem 1rem;
    align-items: stretch;
}

.point-flow__sets {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    justify-content: center;
}

.point-flow__set-btn {
    appearance: none;
    border: 1px solid rgba(15, 23, 42, 0.18);
    background: $color-bg-primary;
    color: $color-text-primary;
    border-radius: $radius-sm;
    padding: 0.45rem 0.7rem;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

    &.is-active {
        background: $color-brand;
        color: $color-text-on-brand;
        border-color: $color-brand;
    }

    &:hover:not(.is-active) {
        border-color: rgba(15, 23, 42, 0.35);
    }
}

.point-flow__chart-wrap {
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
}

.point-flow__chart {
    position: relative;
    display: flex;
    align-items: stretch;
    gap: 6px;
    height: 180px;
    width: max-content;
    min-width: 100%;
    padding: 0 0.15rem;
}

.point-flow__axis {
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 2px;
    margin-top: -1px;
    background: rgba(100, 116, 139, 0.35);
    pointer-events: none;
    z-index: 0;
}

.point-flow__col {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    width: 18px;
    flex: 0 0 18px;
    height: 100%;
}

.point-flow__half {
    flex: 1 1 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-height: 0;
}

.point-flow__half--home {
    justify-content: flex-end;
}

.point-flow__half--away {
    justify-content: flex-start;
}

.point-flow__bar {
    width: 100%;
    flex-shrink: 0;
}

.point-flow__bar--home {
    background: var(--pf-home, #{$color-brand});
    border-radius: 3px 3px 0 0;
}

.point-flow__bar--away {
    background: var(--pf-away, #{$color-info});
    border-radius: 0 0 3px 3px;
}

.point-flow__label {
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1;
    margin: 0.2rem 0;
    color: $color-text-primary;
}

.point-flow__score {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    gap: 0.35rem;
    min-width: 2.5rem;
    font-size: 1.75rem;
    font-weight: 800;
    line-height: 1;
    font-variant-numeric: tabular-nums;
}

.point-flow__score-home {
    color: var(--pf-home, #{$color-brand});
}

.point-flow__score-away {
    color: var(--pf-away, #{$color-info});
}

@include down($bp-sm) {
    .point-flow {
        padding: 0.85rem 0.75rem 1rem;
    }

    .point-flow__body {
        grid-template-columns: auto minmax(0, 1fr);
        grid-template-rows: auto auto;
    }

    .point-flow__sets {
        grid-row: 1 / span 2;
    }

    .point-flow__score {
        grid-column: 2;
        flex-direction: row;
        justify-content: flex-end;
        gap: 0.75rem;
        font-size: 1.4rem;
        min-width: 0;
    }

    .point-flow__chart-wrap {
        grid-column: 2;
    }
}
</style>
